import { Component, ReactNode, MouseEventHandler } from 'react'
import * as cn from 'classnames'
import * as styles from 'components/features/pfm/InternalSidebar/InternalSidebar.css'
import LogoContainer from 'components/features/pfm/Logo/LogoContainer'
import { PageType } from 'interfaces/pages'
import { PAGES_WITHOUT_COMPARISONS } from 'constants/pages'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import SidebarBackButton from 'components/features/pfm/SidebarBackButton/SidebarBackButton'
import MenuButton, { MenuButtonState } from 'components/features/pfm/MenuButton/MenuButton'
import { KebabButton } from 'components/features/pfm/KebabButton/KebabButton'
import { Navigation } from 'components/features/pfm/Navigation/Navigation'
import PeergroupFormContainer from 'components/features/pfm/PeergroupForm/PeergroupFormContainer'
import { SidebarPane, SidebarState } from 'interfaces/sidebar'
import Scrollable from 'components/atoms/Scrollable'
import { ViewportSegment } from 'interfaces/viewport'
import { IS_MOBILE } from 'helpers/userAgentHelpers'
import { convertVhToPx } from 'helpers/styleHelpers'
import { OuterEventTrackerContext, OuterEventTrackerContextType } from 'components/utils/OuterEventTracker'
import withContext from 'hocs/withContext/withContext'
import { AnalyticsClick } from 'components/features/pfm/AnalyticsClick/AnalyticsClick'
import { Peergroups } from 'components/features/pfm/Peergroups/Peergroups'
import { Link } from 'react-router-dom'
import { PreventScroll } from 'components/features/pfm/PreventScroll/PreventScroll'
import { PeergroupPopupPlace } from 'components/features/pfm/PeergroupPopupPlace/PeergroupPopupPlace'
import { NotificationTrayButton } from 'components/features/pfm/NotificationTray/NotificationTrayButton'

export type InternalSidebarStateProps = {
  backPage: PageType | null | 'native'
  previousUrl: string
  currentPage: PageType
  mode: 'common' | 'navigation'

  // these two props may be taken from route props, but it's hard to recreate them in tests
  historyLength: number
  goBack: () => void

  viewportSegment: ViewportSegment
  shownPane: SidebarPane | null
}

export type InternalSidebarDispatchProps = {
  setShownSidebarPane: (shownPane: SidebarState['shownPane']) => void
}

export type InternalSidebarOwnProps = {
  backPage?: PageType | null | 'native'
  hideOnOuterClick?: boolean
  renderGroups?: () => ReactNode
  renderGroupsPopup?: () => ReactNode
}

type InternalSidebarContextProps = {
  outerEventTracker: OuterEventTrackerContextType
}

export type InternalSidebarProps =
  & InternalSidebarStateProps
  & InternalSidebarDispatchProps
  & InternalSidebarOwnProps
  & InternalSidebarContextProps

type InternalSidebarState = {
  backButtonKey: number
  shouldPreventScrollOnOpen: boolean
}

class InternalSidebar extends Component<InternalSidebarProps, InternalSidebarState> {
  state = {
    backButtonKey: 0,
    shouldPreventScrollOnOpen: false,
  }

  private headerNode: HTMLElement | null
  private mainPaneNode: HTMLElement | null
  private menuButtonNode: HTMLElement | null
  private peerButtonNode: HTMLElement | null
  private subscribedOuterClick = false
  private subscribedMobile = false
  private mediaQueryPreventScrollOnOpen = window.matchMedia('screen and (min-width: 415px)')

  private get menuButtonState () {
    const {
      viewportSegment,
      shownPane,
    } = this.props

    let menuButtonState: MenuButtonState = 'hamburger'
    if (viewportSegment !== 'desktop') {
      if (shownPane === 'navigation' || shownPane === 'form') {
        menuButtonState = 'cross'
      }
    } else {
      if (shownPane === 'navigation' || shownPane === 'form') {
        menuButtonState = 'cross'
      }
    }

    return menuButtonState
  }

  render () {
    const { backPage, mode } = this.props

    const internals = mode === 'navigation'
      ? this.renderNavigationSidebar(backPage)
      : this.renderCommonSidebar()

    return (
      <div className={ styles.internalSidebar }>
        { this.renderHeader() }
        { internals }
      </div>
    )
  }

  componentDidMount () {
    if (this.props.viewportSegment === 'desktop') {
      this.subscribeOuterClick()
    }

    this.mediaQueryPreventScrollOnOpen.addListener(this.handleMediaQueryPreventScrollOnOpen)
    this.handleMediaQueryPreventScrollOnOpen()
  }

  componentDidUpdate (prevProps: InternalSidebarProps) {
    const { viewportSegment } = this.props

    // handle set/unset of `shownPane` prop
    if (!prevProps.shownPane && this.props.shownPane) {
      this.subscribeOuterClick()

      if (IS_MOBILE) {
        this.setSizePx()
        this.subscribeMobile()
      }
    } else if (viewportSegment !== 'desktop' && prevProps.shownPane && !this.props.shownPane) {
      this.unsubscribeOuterClick()

      if (IS_MOBILE) {
        this.unsubscribeMobile()
      }
    }

    // handle change of `viewportSegment` prop
    if (prevProps.viewportSegment === 'desktop' && this.props.viewportSegment !== 'desktop' && !this.props.shownPane) {
      // window has been resized to mobile/tablet breakpoint
      this.unsubscribeOuterClick()
    } else if (prevProps.viewportSegment !== 'desktop' && this.props.viewportSegment === 'desktop') {
      // window has been resized to desktop breakpoint
      this.subscribeOuterClick()
    }
  }

  componentWillUnmount () {
    this.mediaQueryPreventScrollOnOpen.removeListener(this.handleMediaQueryPreventScrollOnOpen)
    this.unsubscribeOuterClick()

    if (IS_MOBILE) {
      this.unsubscribeMobile()
    }
  }

  private renderNavigationSidebar = (to: InternalSidebarStateProps['backPage']) => {
    if (to === null) {
      return this.renderNavigationChild()
    }

    if (to === 'native') {
      const { previousUrl, historyLength, goBack } = this.props

      if (historyLength > 1) {
        // if it wasn't opened by direct link
        return (
          <div onClick={ goBack }>
            { this.renderNavigationChild() }
          </div>
        )
      }

      return (
        <Link to={ previousUrl }>
          { this.renderNavigationChild() }
        </Link>
      )
    }

    return (
      <NavigationLink to={ to } onClick={ this.restoreTransform }>
        { this.renderNavigationChild() }
      </NavigationLink>
    )
  }

  private renderNavigationChild () {
    return (
      <div
        ref={ (el) => this.mainPaneNode = el }
        className={ styles.navigation }
      >
        <div
          data-qa-type='internal-sidebar-back-button'
          className={ styles.navigationButton }
        >
          <SidebarBackButton key={ this.state.backButtonKey } />
        </div>
        { this.props.children }
      </div>
    )
  }

  private renderCommonSidebar () {
    const { viewportSegment } = this.props
    const { shouldPreventScrollOnOpen } = this.state

    const viewportShownPane = this.getViewportShownPane()
    const shouldDescendPane = viewportShownPane === 'groups' || viewportShownPane === 'form'
    const shouldNotRenderNav = (viewportSegment !== 'desktop' && viewportShownPane === 'groups') || viewportShownPane === null

    const mainPaneClassName = cn(styles.mainPane, {
      [styles.floating]: viewportSegment === 'tablet',
      [styles.descended]: viewportShownPane !== null,
    })

    const isVisible = viewportShownPane && shouldPreventScrollOnOpen

    return (
      <PreventScroll
        innerRef={ (el) => this.mainPaneNode = el }
        className={ mainPaneClassName }
        allowScroll={ !isVisible }
      >
        <Scrollable heightRelativeToParent='100%' alwaysShowScrollbar={ viewportSegment !== 'desktop' }>
          <div className={ styles.panesWrapper }>
            { !shouldNotRenderNav && <Navigation mode='regular' /> }
            <div className={ cn(styles.descendingPane, { [styles.descended]: shouldDescendPane }) }>
              { viewportShownPane === 'groups' && this.renderGroups() }
              { viewportShownPane === 'form' && <PeergroupFormContainer /> }
            </div>
          </div>
        </Scrollable>
        { viewportShownPane === 'groups' && this.renderGroupsPopup() }
      </PreventScroll>
    )
  }

  private renderGroups () {
    const { renderGroups } = this.props

    return renderGroups
      ? renderGroups()
      : <Peergroups />
  }

  private renderGroupsPopup () {
    const { renderGroupsPopup } = this.props

    return renderGroupsPopup
      ? renderGroupsPopup()
      : <PeergroupPopupPlace />
  }

  private renderHeader () {
    const {
      mode,
      viewportSegment,
      currentPage,
    } = this.props

    if (mode !== 'common') {
      return null
    }

    const shownPane = this.getViewportShownPane()

    const openGroups = shownPane === 'groups'
    const openForm = shownPane === 'form'
    const isDesktopView = viewportSegment === 'desktop'
    const inKebabMode = isDesktopView ? shownPane === 'navigation' : true

    const pageHasComparisons = !PAGES_WITHOUT_COMPARISONS.includes(currentPage)

    const kebabButton = pageHasComparisons && (
      <AnalyticsClick
        category='site_controls-kebab_menu'
        action='click'
        label={ currentPage }
      >
        <div ref={ (node) => { this.peerButtonNode = node } }>
          <KebabButton
            onClick={ this.handlePeergroupsButtonClick }
            expanded={ openGroups || openForm }
            hamburgerMode={ !inKebabMode }
          />
        </div>
      </AnalyticsClick>
    )

    const navigationButton = !isDesktopView && (
      <div className={ styles.menuButton } ref={ (node) => { this.menuButtonNode = node } }>
        <AnalyticsClick
          category='site_controls-hamburger_menu'
          action='click'
        >
          <MenuButton
            state={ this.menuButtonState }
            onClick={ this.handleMenuButtonClick }
          />
        </AnalyticsClick>
      </div>
    )

    return (
      <header
        className={ styles.header }
        ref={ (node) => { this.headerNode = node } }
      >
        <div className={ styles.notificationsTrayButton }>
          <div
            className={ styles.notificationsTrayButtonWrapper }
            data-id='notifications-tray-portal'
          >
            <NotificationTrayButton />
          </div>
        </div>
        <div className={ styles.logo }>
          <LogoContainer theme='white' size='regular' />
        </div>
        <div className={ styles.buttons }>
          { kebabButton }
          { navigationButton }
        </div>
      </header>
    )
  }

  private getViewportShownPane (): SidebarPane | null {
    const { shownPane, viewportSegment } = this.props

    return shownPane === null && viewportSegment === 'desktop'
      ? 'navigation'
      : shownPane
  }

  private subscribeOuterClick = () => {
    if (this.subscribedOuterClick) {
      return
    }

    this.props.outerEventTracker.addOuterEventListener('tap click', this.hideMainPane, this.isOuterEvent)
    if (this.headerNode) {
      this.headerNode.addEventListener('touchend', this.handleTouchend)
    }
    this.subscribedOuterClick = true
  }

  private unsubscribeOuterClick = () => {
    if (!this.subscribedOuterClick) {
      return
    }

    this.props.outerEventTracker.removeOuterEventListener('tap click', this.hideMainPane)
    if (this.headerNode) {
      this.headerNode.removeEventListener('touchend', this.handleTouchend, true)
    }
    this.subscribedOuterClick = false
  }

  private subscribeMobile () {
    if (this.subscribedMobile) {
      return
    }

    window.addEventListener('resize', this.setSizePx)
    this.subscribedMobile = true
  }

  private unsubscribeMobile () {
    if (!this.subscribedMobile) {
      return
    }

    window.removeEventListener('resize', this.setSizePx)
    this.subscribedMobile = false
  }


  private setSizePx = () => {
    if (!this.mainPaneNode || this.props.mode === 'navigation') {
      return
    }

    this.mainPaneNode.style.height = convertVhToPx(styles.mainPaneHeight)
  }

  private restoreTransform = () => {
    this.setState({
      backButtonKey: this.state.backButtonKey + 1,
    })
  }

  private isOuterEvent = (target: Node) => {
    const { mainPaneNode, menuButtonNode, peerButtonNode } = this

    const clickedOutside = Boolean(
      (!mainPaneNode || !mainPaneNode.contains(target))
        && (!menuButtonNode || !menuButtonNode.contains(target))
        && (!peerButtonNode || !peerButtonNode.contains(target))
    )

    return clickedOutside
  }

  private hideMainPane: EventListener = () => {
    const { hideOnOuterClick, setShownSidebarPane, shownPane } = this.props
    if (!hideOnOuterClick) {
      return
    }

    if (shownPane !== null && shownPane !== 'form') {
      // 1) no reason to hide hidden menu
      // 2) collapsing open form by outer event click may be unwanted since it resets user's input
      setShownSidebarPane(null)
    }
  }

  private handleTouchend = (e: TouchEvent) => {
    // prevent ghost click to be triggered; FRO-1385
    if (e.target === this.headerNode) {
      e.preventDefault()
    }
  }

  private handleMenuButtonClick: MouseEventHandler<HTMLElement> = () => {
    const {
      shownPane,
      setShownSidebarPane,
    } = this.props

    const menuButtonState = this.menuButtonState
    if (menuButtonState === 'cross') {
      if (shownPane === 'form') {
        setShownSidebarPane('groups')
      } else if (shownPane === 'navigation') {
        setShownSidebarPane(null)
      }
      return
    }

    setShownSidebarPane('navigation')
  }

  private handlePeergroupsButtonClick = () => {
    const { shownPane, setShownSidebarPane } = this.props

    setShownSidebarPane(shownPane === 'groups' ? null : 'groups')
  }

  private handleMediaQueryPreventScrollOnOpen = () => {
    const shouldPreventScrollOnOpen = !this.mediaQueryPreventScrollOnOpen.matches

    if (shouldPreventScrollOnOpen !== this.state.shouldPreventScrollOnOpen) {
      this.setState({
        shouldPreventScrollOnOpen,
      })
    }
  }
}

export default withContext(OuterEventTrackerContext, 'outerEventTracker')(InternalSidebar)
